import React, { useEffect, useState } from 'react';
import { performanceAPI, employeesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Loader2, Target, Star, Eye, Trash2 } from 'lucide-react';
import { useAuth } from '../App';

const defaultGoal = { name: '', target: '', achieved: '', weight: 1 };
const defaultForm = {
  employee_id: '', review_period: '', overall_rating: 3,
  goals: [{ ...defaultGoal }], strengths: '', improvements: '', manager_comments: '',
};

const Performance = () => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmp, setFilterEmp] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [acting, setActing] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = filterEmp && filterEmp !== 'all' ? { employee_id: filterEmp } : {};
      const [r, e] = await Promise.all([performanceAPI.list(params), employeesAPI.getAll()]);
      setReviews(r.data);
      setEmployees(e.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filterEmp]);

  const openCreate = () => { setForm({ ...defaultForm, goals: [{ ...defaultGoal }] }); setCreateOpen(true); };

  const updateGoal = (i, key, val) => {
    const goals = [...form.goals];
    goals[i] = { ...goals[i], [key]: val };
    setForm({ ...form, goals });
  };
  const addGoal = () => setForm({ ...form, goals: [...form.goals, { ...defaultGoal }] });
  const removeGoal = (i) => setForm({ ...form, goals: form.goals.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.employee_id || !form.review_period) { toast.error('Employee and review period required'); return; }
    setActing(true);
    try {
      const payload = {
        ...form,
        overall_rating: parseFloat(form.overall_rating),
        goals: form.goals.filter(g => g.name).map(g => ({
          ...g,
          weight: parseFloat(g.weight) || 1,
        })),
      };
      await performanceAPI.create(payload);
      toast.success('Review saved');
      setCreateOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setActing(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const avg = reviews.length ? (reviews.reduce((s, r) => s + (r.overall_rating || 0), 0) / reviews.length).toFixed(2) : '—';
  const canCreate = ['super_admin', 'hr_admin', 'manager'].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Target />Performance Reviews</h1>
          <p className="text-muted-foreground">Manager-driven appraisals with KPIs and goals</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterEmp} onValueChange={setFilterEmp}>
            <SelectTrigger className="w-56" data-testid="perf-filter-employee"><SelectValue placeholder="All employees" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {canCreate && <Button onClick={openCreate} data-testid="add-review-btn"><Plus size={16} className="mr-2" />New Review</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total Reviews</p><p className="text-2xl font-bold">{reviews.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Avg Rating</p><p className="text-2xl font-bold text-amber-600 flex items-center gap-1">{avg} <Star size={18} className="fill-amber-500 text-amber-500" /></p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Top Performers (≥4)</p><p className="text-2xl font-bold text-emerald-600">{reviews.filter(r => (r.overall_rating || 0) >= 4).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Needs Improvement (&lt;3)</p><p className="text-2xl font-bold text-red-600">{reviews.filter(r => (r.overall_rating || 0) > 0 && r.overall_rating < 3).length}</p></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Period</TableHead><TableHead>Rating</TableHead>
            <TableHead>Reviewer</TableHead><TableHead>Goals</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {reviews.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No reviews yet</TableCell></TableRow>}
            {reviews.map(r => (
              <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                <TableCell className="font-medium">{r.employee_name || '—'}<div className="text-xs text-muted-foreground">{r.employee_code}</div></TableCell>
                <TableCell>{r.review_period}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <span className="font-bold">{r.overall_rating ?? '—'}</span>
                    {r.overall_rating && <Star size={14} className="fill-amber-500 text-amber-500" />}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{r.reviewer_email || '—'}</TableCell>
                <TableCell>{(r.goals || []).length} goal(s)</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => setViewing(r)} data-testid={`view-review-${r.id}`}><Eye size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Performance Review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                  <SelectTrigger data-testid="review-employee-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Review Period *</Label><Input value={form.review_period} onChange={(e) => setForm({...form, review_period: e.target.value})} placeholder="2026-Q1" data-testid="review-period" /></div>
            </div>
            <div>
              <Label>Overall Rating (1-5)</Label>
              <Input type="number" min="1" max="5" step="0.1" value={form.overall_rating} onChange={(e) => setForm({...form, overall_rating: e.target.value})} data-testid="review-rating" />
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Goals / KPIs</Label>
                <Button type="button" size="sm" variant="outline" onClick={addGoal} data-testid="add-goal-btn"><Plus size={14} /></Button>
              </div>
              {form.goals.map((g, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <Input className="col-span-4" placeholder="Goal name" value={g.name} onChange={(e) => updateGoal(i, 'name', e.target.value)} />
                  <Input className="col-span-3" placeholder="Target" value={g.target} onChange={(e) => updateGoal(i, 'target', e.target.value)} />
                  <Input className="col-span-3" placeholder="Achieved" value={g.achieved} onChange={(e) => updateGoal(i, 'achieved', e.target.value)} />
                  <Input className="col-span-1" type="number" placeholder="Wt" value={g.weight} onChange={(e) => updateGoal(i, 'weight', e.target.value)} />
                  <Button type="button" size="icon" variant="ghost" className="col-span-1" onClick={() => removeGoal(i)}><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>

            <div><Label>Strengths</Label><Textarea value={form.strengths} onChange={(e) => setForm({...form, strengths: e.target.value})} rows={2} /></div>
            <div><Label>Areas of Improvement</Label><Textarea value={form.improvements} onChange={(e) => setForm({...form, improvements: e.target.value})} rows={2} /></div>
            <div><Label>Manager Comments</Label><Textarea value={form.manager_comments} onChange={(e) => setForm({...form, manager_comments: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={acting} data-testid="save-review-btn">{acting && <Loader2 size={14} className="animate-spin mr-1" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{viewing?.employee_name} — {viewing?.review_period}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge variant="default" className="text-base px-3 py-1">Rating: {viewing.overall_rating ?? '—'}/5</Badge>
                <span className="text-sm text-muted-foreground">by {viewing.reviewer_email}</span>
              </div>
              {viewing.goals?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Goals</h3>
                  <Table>
                    <TableHeader><TableRow><TableHead>Goal</TableHead><TableHead>Target</TableHead><TableHead>Achieved</TableHead><TableHead>Weight</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {viewing.goals.map((g, i) => (
                        <TableRow key={i}><TableCell>{g.name}</TableCell><TableCell>{g.target}</TableCell><TableCell>{g.achieved}</TableCell><TableCell>{g.weight}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {viewing.strengths && <div><p className="text-xs text-muted-foreground uppercase">Strengths</p><p className="text-sm whitespace-pre-wrap">{viewing.strengths}</p></div>}
              {viewing.improvements && <div><p className="text-xs text-muted-foreground uppercase">Improvements</p><p className="text-sm whitespace-pre-wrap">{viewing.improvements}</p></div>}
              {viewing.manager_comments && <div><p className="text-xs text-muted-foreground uppercase">Manager Comments</p><p className="text-sm whitespace-pre-wrap">{viewing.manager_comments}</p></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Performance;
